import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import heroBanner from '../assets/hero-banner.jpg';
import {
  STROKE,
  ACCENT,
  NUMERIC,
  STATE,
  SURFACE,
  TEXT,
  type StateName,
} from '../theme/tokens';

// The AirBridge presentational primitives (FD-457).
//
// Everything here is layout and colour over data the pages already hold. None of
// it fetches, and none of it knows a migration from a tenant — the pages do the
// deriving (see ../lib) and hand these plain values.

// ---------------------------------------------------------------------------
// HeroBand — the wing-over-clouds banner on the landing pages.
// ---------------------------------------------------------------------------

/**
 * A token hex as `rgba(...)`, so the hero's fade can reach a partial canvas
 * without a second copy of the canvas value living in a component.
 *
 * FD-471. The alternative — writing `rgba(245, 248, 252, 0.6)` inline — would
 * duplicate `SURFACE.canvas` somewhere the token gate cannot see it (its parser
 * matches `KEY: '#HEX'` and nothing else), so the two could drift and nothing
 * would say so.
 */
function canvasAlpha(hex: string, alpha: number): string {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * The two percentages that keep the headline off the photograph, exported so
 * the relationship between them can be asserted rather than eyeballed.
 *
 * The text column is capped at the first; the overlay is FULLY opaque until the
 * second. While `opaqueUntil >= textMaxWidth`, no headline pixel can land on
 * raw photograph at any band width — both are percentages, so the guarantee
 * scales instead of holding only at whatever width someone happened to check.
 */
export const HERO_TEXT_MAX_WIDTH_PCT = 42;
export const HERO_OPAQUE_UNTIL_PCT = 46;

export interface HeroBandProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
}

// FD-480 — THE PULL-QUOTE IS GONE, PROP AND ALL.
//
// The board floats a line of copy over the photograph — "Organize today.
// Migrate smarter." and five siblings. It is marketing voice on a surface
// `frontend/CLAUDE.md` defines as the opposite: *"a precision instrument, not a
// marketing site."* An operator managing a 2,000-device wave at 11pm is not an
// audience for a tagline.
//
// The PROP goes with the strings deliberately. Deleting six call-site arguments
// and leaving `quote?: string` here would leave the next hero one keystroke
// from a seventh, and the thing would return half-applied — the FD-270 pattern,
// where a rule was inverted, three documents were corrected, and four in-code
// statements of the old rule survived anyway.
export function HeroBand({ eyebrow, title, subtitle }: HeroBandProps) {
  return (
    <section
      className="relative mb-6 overflow-hidden rounded-lg"
      // FD-471 — the ground is the PAGE CANVAS, not navy. The band's left edge is
      // therefore the same value as the page around it, which is what makes the
      // photograph read as fading INTO the page rather than sitting in a box on
      // top of it.
      style={{ backgroundColor: SURFACE.canvas }}
    >
      <img
        src={heroBanner}
        alt=""
        aria-hidden="true"
        loading="eager"
        className="absolute inset-0 h-full w-full object-cover"
      />
      {/* The scrim inverted, and the reason for having one at all is unchanged:
          without a controlled overlay the contrast of the headline is whatever
          the photograph's pixels happen to be, which is not a number anyone can
          check. What changed is which side is opaque.

          The two bounds are named constants above and their relationship is
          asserted, so the headline cannot sit on raw photograph at any width —
          both are percentages, so they hold together as the band resizes rather
          than only at the width someone tested. Ink on canvas is 15.2:1,
          which is better than the white-on-navy this replaces, not a
          concession. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background: `linear-gradient(90deg, ${SURFACE.canvas} 0%, ${SURFACE.canvas} ${HERO_OPAQUE_UNTIL_PCT}%, ${canvasAlpha(
            SURFACE.canvas,
            0.65,
          )} 64%, ${canvasAlpha(SURFACE.canvas, 0)} 88%)`,
        }}
      />
      <div className="relative flex items-center px-8 py-8">
        <div className="min-w-0" style={{ maxWidth: `${HERO_TEXT_MAX_WIDTH_PCT}%` }}>
          {eyebrow && (
            <div
              className="mb-2 text-[10px] font-semibold uppercase"
              style={{ color: TEXT.secondary, letterSpacing: '0.12em' }}
            >
              {eyebrow}
            </div>
          )}
          <h1
            className="m-0 text-[26px] font-semibold leading-tight"
            style={{ color: TEXT.primary }}
          >
            {title}
          </h1>
          {subtitle && (
            <p className="mt-2 text-[13px] leading-relaxed" style={{ color: TEXT.secondary }}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// PageHeader — the non-hero page title.
// ---------------------------------------------------------------------------

export function PageHeader({
  title,
  subtitle,
  badge,
  actions,
  facts,
}: {
  title: string;
  subtitle?: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
  /** The chip row the campaign mockups put under the title. */
  facts?: ReactNode;
}) {
  return (
    <header className="mb-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="m-0 text-[22px] font-semibold" style={{ color: TEXT.primary }}>
              {title}
            </h1>
            {badge}
          </div>
          {subtitle && (
            <div className="mt-1.5 text-[13px]" style={{ color: TEXT.secondary }}>
              {subtitle}
            </div>
          )}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      {facts && <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-3">{facts}</div>}
    </header>
  );
}

/** One labelled fact in the PageHeader chip row. */
export function HeaderFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div
        className="text-[10px] font-semibold uppercase"
        style={{ color: TEXT.secondary, letterSpacing: '0.08em' }}
      >
        {label}
      </div>
      <div className="mt-0.5 truncate text-[13px]" style={{ color: TEXT.primary }}>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MetricCard — the summary tiles.
// ---------------------------------------------------------------------------

export interface MetricCardProps {
  label: string;
  /** Pre-formatted. Rendered with tabular figures so a row of cards aligns. */
  value: ReactNode;
  caption?: ReactNode;
  icon?: ReactNode;
  tone?: StateName;
}

// NOTE: no `delta` slot, and that is deliberate (FD-ADR-043 D4). The mockups
// show "↑ 12%" on every tile; nothing in the platform stores a previous value,
// so every one of those numbers would have to be invented.
export function MetricCard({ label, value, caption, icon, tone }: MetricCardProps) {
  const accent = tone ? STATE[tone] : null;
  return (
    <div
      className="flex items-start gap-3 rounded-lg border px-4 py-3.5"
      style={{ borderColor: STROKE.base, backgroundColor: SURFACE.base }}
    >
      {icon && (
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
          style={{
            backgroundColor: accent ? accent.soft : ACCENT.tint,
            color: accent ? accent.text : ACCENT.pressed,
          }}
        >
          {icon}
        </div>
      )}
      <div className="min-w-0">
        <div className="text-[12px] font-medium" style={{ color: TEXT.secondary }}>
          {label}
        </div>
        <div
          className="mt-0.5 text-[24px] font-semibold leading-none"
          style={{ color: TEXT.primary, ...NUMERIC }}
        >
          {value}
        </div>
        {caption && (
          <div className="mt-1.5 text-[11px]" style={{ color: TEXT.secondary }}>
            {caption}
          </div>
        )}
      </div>
    </div>
  );
}

export function MetricRow({ children }: { children: ReactNode }) {
  return (
    <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">{children}</div>
  );
}

// ---------------------------------------------------------------------------
// StatePill — the status badge.
// ---------------------------------------------------------------------------

export function StatePill({
  state,
  children,
  dot = true,
}: {
  state: StateName;
  children: ReactNode;
  dot?: boolean;
}) {
  const tone = STATE[state];
  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium"
      // The LABEL is painted in `tone.text`, never `tone.glyph`. Every glyph hex
      // fails AA as text on its own soft tint (2.77–3.31:1); the text rungs clear
      // it (4.80–6.46:1). theme/contrast.test.ts asserts both directions.
      style={{ backgroundColor: tone.soft, color: tone.text }}
    >
      {dot && (
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          // The dot may use the glyph hex: it is a graphic, not text.
          style={{ backgroundColor: tone.glyph }}
        />
      )}
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// SectionCard — a titled content panel.
// ---------------------------------------------------------------------------

export function SectionPanel({
  title,
  description,
  action,
  children,
  bodyClassName,
}: {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  bodyClassName?: string;
}) {
  return (
    <section
      className="mb-6 overflow-hidden rounded-lg border"
      style={{ borderColor: STROKE.base, backgroundColor: SURFACE.base }}
    >
      {(title || action) && (
        <header className="flex flex-wrap items-center justify-between gap-3 px-5 pb-3 pt-4">
          <div className="min-w-0">
            {title && (
              <h2 className="m-0 text-[15px] font-semibold" style={{ color: TEXT.primary }}>
                {title}
              </h2>
            )}
            {description && (
              <p className="mt-0.5 text-[12px]" style={{ color: TEXT.secondary }}>
                {description}
              </p>
            )}
          </div>
          {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
        </header>
      )}
      <div className={bodyClassName ?? 'px-5 pb-5'}>{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// FactList — the label/value rows in a context card.
// ---------------------------------------------------------------------------

export function FactList({ children }: { children: ReactNode }) {
  return <dl className="m-0 flex flex-col gap-2.5">{children}</dl>;
}

export function Fact({
  label,
  children,
  numeric,
}: {
  label: string;
  children: ReactNode;
  numeric?: boolean;
}) {
  const style: CSSProperties = { color: TEXT.primary, ...(numeric ? NUMERIC : {}) };
  return (
    <div className="grid grid-cols-[minmax(0,110px)_minmax(0,1fr)] items-baseline gap-3">
      <dt className="m-0 text-[12px]" style={{ color: TEXT.secondary }}>
        {label}
      </dt>
      <dd className="m-0 min-w-0 break-words text-[12px] font-medium" style={style}>
        {children}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EmptyState — and the reason it takes a REASON.
// ---------------------------------------------------------------------------

export function EmptyState({
  title,
  detail,
  action,
}: {
  title: string;
  /** Why there is nothing here. An empty table with no explanation is a bug report. */
  detail?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="px-5 py-10 text-center">
      <p className="m-0 text-[14px] font-medium" style={{ color: TEXT.primary }}>
        {title}
      </p>
      {detail && (
        <p
          className="mx-auto mt-1.5 max-w-[52ch] text-[12px] leading-relaxed"
          style={{ color: TEXT.secondary }}
        >
          {detail}
        </p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * An inline link in AirBridge blue — the `pressed` rung, not `base`.
 *
 * FD-467 settled a contradiction FD-466 left in the tree: `tokens.ts`, the
 * published 2.0.0 contract and the PowerShell gate all say accent TEXT and
 * LINKS use `pressed`, while these primitives shipped `base`. `base` is 4.24:1
 * on white — the same shortfall as the filled button, which is accepted under
 * D5 because white on the brand is unavoidable there. Link text has no such
 * constraint, and `pressed` costs nothing: the ramp is untouched, the filled
 * CTA is untouched, so D5 is not reversed by this.
 */
export function AbLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="no-underline hover:underline" style={{ color: ACCENT.pressed }}>
      {children}
    </Link>
  );
}
